import { type Hierarchical, type TextMessage } from "anketa-core";

/**
 * Render hints for catalog items, kept OUTSIDE the anketa-core items.
 *
 * Catalog structure defines the fact paths the parser reads
 * (e.g. `networks.agents.agent_1.bgp_settings.bgp_ip`), so a nicer layout must
 * not be bought by regrouping items. These hints only change how an item is
 * drawn: a Row can be shown as a (collapsible) card, and its questions can be
 * bucketed into labelled, collapsible field groups inside that card.
 */

export interface CardBadge {
  label: TextMessage;
  color?: "hollow" | "default" | "primary" | "accent" | "warning" | "danger";
}

export interface CardOptions {
  /** Collapse the card body behind its title (EuiAccordion). */
  collapsible?: boolean;
  /**
   * Whether a collapsible card starts open. Default: open while something in
   * it is still open or invalid, closed once it is complete.
   */
  initialIsOpen?: () => boolean;
  /** One-line summary shown next to the title, readable while collapsed. */
  summary?: () => string;
  /** Status chips shown next to the title (e.g. "removed", "room B"). */
  badges?: () => CardBadge[];
  /** Field groups inside the card can be folded (default true). */
  collapsibleGroups?: boolean;
}

const cards = new WeakMap<object, CardOptions>();
const fieldGroups = new WeakMap<object, { key: string; label: TextMessage }>();

/** Show this Row as a bordered card with an aligned field grid. */
export const presentAsCard = (
  row: Hierarchical,
  options: CardOptions = {},
): void => {
  cards.set(row, options);
};

export const isCard = (row: Hierarchical): boolean => cards.has(row);

export const cardOptions = (row: Hierarchical): CardOptions =>
  cards.get(row) ?? {};

/**
 * Put a child of a card Row into a labelled group. Consecutive children with the
 * same key share one heading and grid; children without a group are drawn
 * without a heading.
 */
export const setFieldGroup = (
  item: Hierarchical,
  key: string,
  label: TextMessage,
): void => {
  fieldGroups.set(item, { key, label });
};

export const fieldGroupOf = (
  item: Hierarchical,
): { key: string; label: TextMessage } | undefined => fieldGroups.get(item);
